import type { NetworkTypeKey } from "./Network";
import { NetworkReverseMap } from "./Network";
import { ScriptType } from "./ScriptType";
import { DerivationPurposeString } from './Purpose';

/**
 * Derivation Path Variant Module
 * 
 * This module combines BIP standards, script types, and networks to create complete
 * derivation path identifiers. Each variant represents a specific combination that
 * determines how Bitcoin addresses are derived and what format they use.
 * 
 * Example identifiers: "BIP84_P2WPKH_MAINNET", "BIP49_P2SH_P2WPKH_TESTNET", etc.
 */

// ============================================================================
// 1. DERIVATION VARIANTS: BIP + Script Type Combinations
// ============================================================================

/**
 * List of all supported derivation variants (without network).
 * Each variant combines a BIP standard with its corresponding script type.
 * 
 * Supported combinations:
 * - ELECTRUM_LEGACY_P2PKH: Electrum legacy wallet format
 * - BIP_44_P2PKH: Legacy addresses
 * - BIP_49_P2SH_P2WPKH: Nested SegWit addresses
 * - BIP_84_P2WPKH: Native SegWit addresses
 * - BIP_86_P2TR: Taproot addresses
 * - BIP_45_P2SH_MULTISIG: Multi-signature legacy addresses
 * - BIP_48_P2WSH_P2SH: Wrapped witness script hash (multisig SegWit)
 * - BIP_48_P2WSH: Native witness script hash (multisig native SegWit)
 */
export const DERIVATION_VARIANTS = [
    `ELECTRUM_LEGACY_${ScriptType.P2PKH}`.toUpperCase(),
    `${DerivationPurposeString.BIP_44}_${ScriptType.P2PKH}`.toUpperCase(),
    `${DerivationPurposeString.BIP_49}_${ScriptType.P2SH_P2WPKH}`.toUpperCase(),
    `${DerivationPurposeString.BIP_84}_${ScriptType.P2WPKH}`.toUpperCase(),
    `${DerivationPurposeString.BIP_86}_${ScriptType.P2TR}`.toUpperCase(),
    `${DerivationPurposeString.BIP_45}_${ScriptType.P2SH_MULTISIG}`.toUpperCase(),
    `${DerivationPurposeString.BIP_48}_${ScriptType.P2WSH_P2SH}`.toUpperCase(),
    `${DerivationPurposeString.BIP_48}_${ScriptType.P2WSH}`.toUpperCase(),
] as const;

/**
 * Union type of all derivation variants (without network).
 * Equivalent to: "ELECTRUM_LEGACY_P2PKH" | "BIP_44_P2PKH" | "BIP_49_P2SH-P2WPKH" | ...
 */
export type DerivationVariant = typeof DERIVATION_VARIANTS[number];

// ============================================================================
// 2. NETWORK-SPECIFIC TYPES
// ============================================================================

/**
 * Template type that creates a complete derivation ID by combining a variant with a network.
 * @template N - Network type key (MAINNET | TESTNET | REGTEST)
 * @example "BIP84_P2WPKH_MAINNET", "BIP49_P2SH-P2WPKH_TESTNET"
 */
type DerivationIdForNetwork<N extends NetworkTypeKey> = `${DerivationVariant}_${N}`;

/**
 * Map type for a specific network where keys and values are identical derivation IDs.
 * This creates an enum-like structure for type-safe ID access.
 * @template N - Network type key
 * @example { BIP84_P2WPKH_MAINNET: "BIP84_P2WPKH_MAINNET", ... }
 */
type DerivationPathMapForNetwork<N extends NetworkTypeKey> = { [K in DerivationIdForNetwork<N>]: K; };

// ============================================================================
// 3. BUILDER FUNCTION
// ============================================================================

/**
 * Creates a complete derivation path map for a specific network.
 * This function generates all possible variant + network combinations.
 * 
 * @template N - Network type key
 * @param networkKey - The network to create the map for (MAINNET, TESTNET, or REGTEST)
 * @returns An object mapping each derivation ID to itself for type-safe access
 * @internal
 */
function buildDerivationPathMapForNetwork<N extends NetworkTypeKey>(networkKey: N): DerivationPathMapForNetwork<N> {
    const entries = DERIVATION_VARIANTS.map((variant) => {
        const id = `${variant}_${networkKey}` as const;
        return [id, id];
    });
    return Object.fromEntries(entries) as DerivationPathMapForNetwork<N>;
}

// ============================================================================
// 4. NETWORK-SPECIFIC DERIVATION PATH ID MAPS
// ============================================================================

/**
 * All derivation path IDs for mainnet.
 * Access like: DerivationPathIDMainnet.BIP84_P2WPKH_MAINNET
 */
export const DerivationPathIDMainnet = buildDerivationPathMapForNetwork(NetworkReverseMap.mainnet);

/**
 * All derivation path IDs for testnet.
 * Access like: DerivationPathIDTestnet.BIP84_P2WPKH_TESTNET
 */
export const DerivationPathIDTestnet = buildDerivationPathMapForNetwork(NetworkReverseMap.testnet);

/**
 * All derivation path IDs for regtest.
 * Access like: DerivationPathIDRegtest.BIP84_P2WPKH_REGTEST
 */
export const DerivationPathIDRegtest = buildDerivationPathMapForNetwork(NetworkReverseMap.regtest);

// ============================================================================
// 5. VALUE TYPES (for type checking and validation)
// ============================================================================

/**
 * Union type of all mainnet derivation path IDs.
 * Useful for function parameters and return types.
 */
export type DerivationPathIDMainnetValue = (typeof DerivationPathIDMainnet)[keyof typeof DerivationPathIDMainnet];

/**
 * Union type of all testnet derivation path IDs.
 * Useful for function parameters and return types.
 */
export type DerivationPathIDTestnetValue = (typeof DerivationPathIDTestnet)[keyof typeof DerivationPathIDTestnet];

/**
 * Union type of all regtest derivation path IDs.
 * Useful for function parameters and return types.
 */
export type DerivationPathIDRegtestValue = (typeof DerivationPathIDRegtest)[keyof typeof DerivationPathIDRegtest];
